import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild, inject } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { DownlineTreeviewItem, OrderDownlineTreeviewEventParser, TreeviewComponent, TreeviewConfig, TreeviewEventParser, TreeviewItem, TreeviewItemTemplateContext } from '@laijuthomas/ngx-treeview';
import { getRandomId } from 'src/app/shared/extensions/string.extensions';
import { ISelectedTreeviewItemTooltip } from '../standard-treeview/models/selected-treeview-item-tooltip.interface';
import { ITreeViewItemValue } from '../standard-treeview/models/treeview-item-value.interface';
import { ITreeviewItem } from '../standard-treeview/models/treeview-item.interface';
import { ISelectedTreeviewItem } from '../standard-treeview/selected-treeview-item.interface';

@Component({
    selector: 'app-standard-single-selection-treeview',
    templateUrl: './standard-single-selection-treeview.component.html',
    styleUrls: ['./standard-single-selection-treeview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: TreeviewEventParser, useClass: OrderDownlineTreeviewEventParser }],
    standalone: false,
})
export class StandardSingleSelectionTreeviewComponent implements ControlValueAccessor, OnInit, OnChanges {
    public ngControl = inject(NgControl, { self: true, optional: true });

    @Input() public label: string;
    @Input() public hint: string;
    @Input() public infoText: string;
    @Input() public disabled = false;
    @Input() public hasFilter: boolean;
    @Input() public hasCollapseExpand: boolean;
    @Input() public decoupleChildFromParent: boolean;
    @Input() public itemSeperator = '|';
    @Input() public maxHeight = 200;
    @Input() public loading: boolean;

    private _items: TreeviewItem[];
    @Input() public set items(value: TreeviewItem[]) {
        this._items = value.map((item) => new TreeviewItem(item));
    }

    public get items(): TreeviewItem[] {
        return this._items;
    }

    @Input() public itemTemplate: TemplateRef<TreeviewItemTemplateContext>;

    public value: ITreeViewItemValue;
    public id: string;
    public config: TreeviewConfig;
    public selectedItemsToShow: ISelectedTreeviewItem;
    private readonly hasAllCheckBox = false;
    @ViewChild(TreeviewComponent, { static: false }) public treeviewComponent: TreeviewComponent;

    constructor() {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }

        this.id = getRandomId();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const loadingChange = changes.loading;

        if (loadingChange?.previousValue != loadingChange?.currentValue && this.items) {
            this.updateItems(this.items, this.value);
        }
    }

    public ngOnInit(): void {
        this.config = TreeviewConfig.create({
            hasAllCheckBox: this.hasAllCheckBox,
            hasFilter: this.hasFilter,
            hasCollapseExpand: this.hasCollapseExpand,
            decoupleChildFromParent: this.decoupleChildFromParent,
            maxHeight: this.maxHeight,
        });
    }

    public onChangeFn = (_: object) => {
        return;
    };

    public onTouchedFn = () => {
        return;
    };

    public registerOnChange(fn: () => void): void {
        this.onChangeFn = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public writeValue(value: ITreeViewItemValue): void {
        if (value != null) {
            this.value = value;
        } else {
            this.value = null;
        }

        this.updateItems(this.items, this.value);
    }

    public getLatestSelectedItem(downlineItems: DownlineTreeviewItem[]) {
        if (!downlineItems?.length) {
            return;
        }

        return downlineItems.find((downlineItem) => this.value?.id !== downlineItem.item.value['id']) ?? downlineItems[0];
    }

    public onSelectionChange(downlineItems: DownlineTreeviewItem[]) {
        let selectedItemsToShow: ISelectedTreeviewItem = null;
        const latestSelectedItem = this.getLatestSelectedItem(downlineItems);

        if (latestSelectedItem) {
            this.items?.forEach((item) => this.updateCheckboxState(item, latestSelectedItem.item.value['id']));
            const downlineItem = latestSelectedItem;
            const item = downlineItem.item;
            const value = item.value;
            const texts = [item.text];
            let parent = downlineItem.parent;

            while (parent) {
                texts.push(parent.item.text);
                parent = parent.parent;
            }

            const reverseTexts = texts.reverse();
            const displayText = reverseTexts.join(` ${this.itemSeperator} `);

            selectedItemsToShow = { text: displayText, value, tooltip: { parentItemText: reverseTexts[0], childItemText: reverseTexts[1] } as ISelectedTreeviewItemTooltip } as ISelectedTreeviewItem;
        }

        this.selectedItemsToShow = selectedItemsToShow;

        this.value = this.getValue();
        this.onChangeFn(this.value);
    }

    private updateCheckboxState(item: TreeviewItem, selectedId: string) {
        if (!item) {
            return;
        }

        item.checked = item.value.id == selectedId;
        item.children?.forEach((child) => this.updateCheckboxState(child, selectedId));
    }

    private getValue() {
        if (this.selectedItemsToShow) {
            return { ...this.selectedItemsToShow.value, name: this.selectedItemsToShow.text };
        }

        return null;
    }

    public removeItem(event: Event) {
        event.preventDefault();
        event.stopPropagation();

        this.selectedItemsToShow = null;
        this.value = this.getValue();

        this.updateItems(this.items, this.value);
        this.onChangeFn(this.value);
    }

    private updateItems(items: TreeviewItem[], selected: ITreeViewItemValue) {
        this.items = items?.map((item) => this.mapItem(item, selected));
    }

    private mapItem(item: TreeviewItem, selected: ITreeViewItemValue): TreeviewItem {
        if (item.children && item.children.length > 0) {
            return this.updateTreeParent(item, selected);
        }

        return this.updateTreeLeaf(item, selected);
    }

    private updateTreeParent = (item: TreeviewItem, selected: ITreeViewItemValue) => {
        const children = item.children.map((child) => this.mapItem(child, selected));

        if (JSON.stringify(children) == JSON.stringify(item.children)) {
            return item;
        }

        const modifiedParent = {
            ...item,
            children,
        };

        return new TreeviewItem(modifiedParent, true);
    };

    private updateTreeLeaf = (item: ITreeviewItem, selected: ITreeViewItemValue) => {
        const shouldBeChecked = selected?.id == item.value.id;

        if (item.checked === shouldBeChecked) {
            return item;
        }

        const modifiedItem = {
            ...item,
            checked: shouldBeChecked,
        };

        return new TreeviewItem(modifiedItem);
    };
}
